import { cn } from '@src/design-system/helpers';
import { useSetViewAsDefault } from '../services/set-view-as-default';
import { Loader2, Pin } from 'lucide-react';
import type { ComponentProps } from 'react';
import viewApi from '../services/view-api';

export const MarkViewAsDefaultBtn = ({
  staticFilterId,
  isDefault,
  onToggle,
  className,
  ...props
}: {
  staticFilterId: number | string;
  isDefault: boolean;
  /** Called immediately so the parent can update before the views query refetches. */
  onToggle?: (isDefault: boolean) => void;
} & ComponentProps<'button'>) => {
  const { mutate: setViewAsDefault, isPending } = useSetViewAsDefault();
  const { mutate: updateView, isPending: isUpdating } = viewApi.useUpdate();

  return (
    <button
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        if (isPending || isUpdating) return;

        const next = !isDefault;
        onToggle?.(next);

        if (!next) {
          updateView(
            {
              id: staticFilterId,
              is_default: false,
            },
            { onError: () => onToggle?.(true) }
          );
          return;
        }

        setViewAsDefault(staticFilterId, {
          onError: () => onToggle?.(false),
        });
      }}
      className={cn(
        'cursor-pointer shrink-0 flex justify-center items-center size-6 bg-secondary-50 rounded-lg text-secondary-1000 hover:bg-secondary-100',
        className
      )}
    >
      {isPending || isUpdating ? (
        <Loader2
          className='animate-spin'
          size={16}
        />
      ) : (
        <Pin
          size={16}
          className={cn('rotate-45', isDefault && 'text-secondary-1000')}
          {...(isDefault && { fill: 'currentColor' })}
        />
      )}
    </button>
  );
};
